// HashLab.js
import React, { useState, useEffect, useRef } from "react"; // add useRef
import "./HashLab.css";

const API_URL =
  "https://op5a5iw1c1.execute-api.ap-south-1.amazonaws.com/dev/sql-injection";
// this is your Lambda API endpoint; we send { method:"hash", ... }

async function callApi(payload) {
  const res = await fetch(API_URL, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ method: "hash", ...payload }),
  });

  const text = await res.text();
  // API Gateway sometimes wraps response; parse robustly
  let outer;
  try {
    outer = JSON.parse(text);
  } catch (e) {
    throw new Error("Backend did not return JSON: " + text);
  }
  // API Gateway wrapper: outer.body may be stringified JSON
  const inner =
    typeof outer.body === "string"
      ? JSON.parse(outer.body)
      : outer.body || outer;
  if (outer.statusCode && outer.statusCode >= 400) {
    throw new Error(inner && inner.error ? inner.error : "Request failed");
  }
  return inner;
}

export default function HashLab() {
  const [password, setPassword] = useState("");
  const [algo, setAlgo] = useState("sha256");
  const [iterations, setIterations] = useState(100000);
  const [terminal, setTerminal] = useState("");
  const [hashSpec, setHashSpec] = useState(null);
  const [wordlist, setWordlist] = useState([]);
  const [crackResult, setCrackResult] = useState(null);
  const [isWorking, setIsWorking] = useState(false);
  const terminalRef = useRef(null);

  useEffect(() => {
    if (terminalRef.current) {
      terminalRef.current.scrollTop = terminalRef.current.scrollHeight;
    }
  }, [terminal]); 

  const sampleWordlists = {
    Tiny: ["123456", "password", "degen1234", "coffee@123"],
    Common: [
      "123456",
      "password",
      "qwerty",
      "abc123",
      "letmein",
      "welcome2024",
      "admin",
      "degen1234",
    ],
  };

  const handleGenHash = async () => {
    setTerminal((t) => t + `[*] Generating ${algo} hash...\n`);
    try {
      const data = await callApi({
        action: "gen_hash",
        algo,
        password,
        iterations,
      });
      setTerminal((t) => t + (data.terminal || ""));
      setHashSpec(data.hash_spec || null);
      setCrackResult(null);
    } catch (e) {
      setTerminal((t) => t + `[!] Error: ${e.message}\n`);
    }
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const raw = ev.target.result;
      const lines = raw
        .split(/\r?\n/)
        .map((s) => s.trim())
        .filter(Boolean);
      setWordlist(lines);
      setTerminal((t) => t + `[*] Loaded wordlist: ${lines.length} entries\n`);
    };
    reader.readAsText(file);
  };

  const handleCrack = async (mode = "dictionary") => {
    // ensure we have either a hashSpec or a target hash
    if (!hashSpec) {
      setTerminal((t) => t + "[!] No hash present. Generate a hash first.\n");
      return;
    }
    if (wordlist.length === 0 && mode === "dictionary") {
      setTerminal(
        (t) => t + "[!] No wordlist selected. Use sample list or upload file.\n"
      );
      return;
    }

    setIsWorking(true);
    setTerminal((t) => t + `[*] Starting crack (${mode})...\n`);
    try {
      const data = await callApi({
        action: "crack",
        hash_spec: hashSpec,
        wordlist: mode === "dictionary" ? wordlist : [],
        mode,
        max_attempts: 50000,
      });
      setTerminal((t) => t + (data.terminal || ""));
      setCrackResult(data.result || null);
    } catch (e) {
      setTerminal((t) => t + `[!] Error: ${e.message}\n`);
    } finally {
      setIsWorking(false);
    }
  };

  const handlePickSample = (name) => {
    setWordlist(sampleWordlists[name] || []);
    setTerminal(
      (t) =>
        t +
        `[*] Picked sample wordlist '${name}'. ${sampleWordlists[name].length} words loaded.\n`
    );
  };

  return (
    <div className="hashlab-container">
      <h1 className="hashlab-title">🔐 Password & Hash Lab</h1>
      <p className="hashlab-sub">
        Generate hashes, upload a wordlist, attempt dictionary or small
        bruteforce cracks — fully simulated for learning.
      </p>

      <div className="hashlab-grid">
        {/* Left: generator + wordlist */}
        <div className="panel">
          <h2>Generate Hash</h2>
          <label>Password</label>
          <input
            type="text"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Enter password to hash"
            className="hash-input"
          />

          <label>Algorithm</label>
          <select value={algo} onChange={(e) => setAlgo(e.target.value)}>
            <option value="md5">MD5 (fast)</option>
            <option value="sha1">SHA1 (fast)</option>
            <option value="sha256">SHA256 (fast)</option>
            <option value="pbkdf2_sha256">PBKDF2-SHA256 (slow)</option>
            <option value="bcrypt">Bcrypt-sim (slow)</option>
            <option value="argon2">Argon2-sim (slow)</option>
          </select>

          {(algo === "pbkdf2_sha256" ||
            algo === "bcrypt" ||
            algo === "argon2") && (
            <>
              <label>Iterations</label>
              <input
                type="number"
                value={iterations}
                onChange={(e) => setIterations(Number(e.target.value))}
              />
            </>
          )}

          <div className="row">
            <button onClick={handleGenHash} disabled={!password || isWorking}>
              Generate
            </button>
            <button
              onClick={() => {
                setPassword("");
                setHashSpec(null);
                setTerminal("");
                setWordlist([]);
                setCrackResult(null);
              }}
            >
              Reset
            </button>
          </div>

          {hashSpec && (
            <div className="hash-output">
              <div>
                <strong>Algo:</strong> {hashSpec.algo}
              </div>
              <div>
                <strong>Salt:</strong> {hashSpec.salt || "-"}
              </div>
              <div>
                <strong>Iterations:</strong> {hashSpec.iterations || "-"}
              </div>
              <div className="mono">
                <strong>Hash:</strong> {hashSpec.hash}
              </div>
            </div>
          )}
        </div>

        {/* Right: terminal */}
        <div className="panel terminal-panel">
          <h2>Terminal</h2>
          <pre ref={terminalRef}  className="terminal">{terminal || "Ready.\n"}</pre>
        </div>

        {/* Middle: wordlist / attack */}
        <div className="panel">
          <h2>Wordlist & Attack</h2>
          <label>Upload wordlist (.txt, one password per line)</label>
          <input type="file" accept=".txt" onChange={handleFileUpload} />
          <div className="small-note">
            File will be read client-side; don't upload huge lists (keep &lt;
            50000 lines).
          </div>

          <div className="sample-lists">
            <div>Samples:</div>
            <button onClick={() => handlePickSample("Tiny")}>Tiny</button>
            <button onClick={() => handlePickSample("Common")}>Common</button>
            <div className="small-note">
              Loaded list size: {wordlist.length}
            </div>
          </div>

          <div className="attack-row">
            <button
              onClick={() => handleCrack("dictionary")}
              disabled={!hashSpec || isWorking}
            >
              Start Dictionary Crack
            </button>
            <button
              onClick={() => handleCrack("bruteforce")}
              disabled={!hashSpec || isWorking}
            >
              Small Bruteforce (sim)
            </button>
          </div>

          {crackResult && (
            <div className="result">
              <div>
                <strong>Success:</strong> {String(crackResult.success)}
              </div>
              <div>
                <strong>Attempts:</strong> {crackResult.attempts}
              </div>
              <div>
                <strong>Time:</strong> {crackResult.seconds}s
              </div>
              {crackResult.password && (
                <div className="mono">
                  <strong>Password:</strong> {crackResult.password}
                </div>
              )}
            </div>
          )}
        </div>

        
      </div>

      <div className="footer-note">
        This is an educational simulator. For large/real cracking, use proper
        offline tools and legal lab environments.
      </div>
    </div>
  );
}
